import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Coins, Sparkles } from 'lucide-react';
import MarketingPage, { ContentCard } from '@/components/marketing/MarketingPage';
import { pageJsonLd, faqJsonLd, SITE, SOFTWARE_APP_JSONLD } from '@/components/shared/Seo';

const TIERS = [
  { id: 'starter', name: 'Starter', price: 'Free', desc: 'Get started with AI matching. No card required.',
    features: ['Up to 50 candidate profiles', '20 AI interview minutes with Jay', 'Basic reporting', 'Email support'], cta: 'Start free' },
  { id: 'growth', name: 'Growth', price: 'Mu Points', highlighted: true, desc: 'Pay per action — matches, interviews, reports.',
    features: ['Unlimited candidate profiles', 'Pay-per-use Jay interviews', 'Vector matching + hot tags', 'Priority support', 'Team hierarchy & permissions'], cta: 'Buy Mu Points' },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', desc: 'For staffing groups and larger hiring teams.',
    features: ['Everything in Growth', 'SSO & audit logs', 'Custom SLAs', 'Dedicated success manager', 'API access & data residency'], cta: 'Contact sales' },
];

const FAQ = [
  { q: 'How much does Numuv cost?',
    a: 'Numuv is usage-based, not per seat. The Starter tier is free with no card required. The Growth tier is pay-per-action using Mu Points credits, and Enterprise pricing is custom. There are no annual commitments or seat minimums.' },
  { q: 'What are Mu Points?',
    a: 'Mu Points (Mu) are Numuv\u2019s usage credits. Every AI action \u2014 matching a candidate, generating screening questions, running an AI interview, producing a report \u2014 spends a defined number of Mu. You top up in INR and spend only what you use.' },
  { q: 'Do Mu Points expire?',
    a: 'No. Purchased Mu stay in your wallet until you spend them. Recurring charges such as job renewals are shown in the ledger before and after they are applied.' },
  { q: 'What happens if an AI action fails?',
    a: 'You are never charged for a failed AI action. Numuv either does not debit or automatically refunds the Mu, and every debit and refund is visible in your Mu ledger.' },
  { q: 'Is Numuv free for job seekers?',
    a: 'Yes. Applicants build a profile, get AI matches and track their journey for free. Optional extras like additional interview-prep questions are charged in Mu, always after an explicit confirmation.' },
  { q: 'Which payment methods are supported?',
    a: 'Mu Points are purchased in Indian rupees through Razorpay, which supports UPI, cards, net banking and wallets. Enterprise customers can be invoiced \u2014 contact hello@numuv.app.' },
];

export default function Pricing() {
  const jsonLd = pageJsonLd({
    name: 'Numuv pricing',
    description: 'Usage-based pricing built on Mu Points credits. Start free, pay per AI action, custom Enterprise plans.',
    path: '/pricing',
    breadcrumbs: [{ name: 'Pricing', path: '/pricing' }],
    extra: [
      SOFTWARE_APP_JSONLD,
      {
        '@type': 'OfferCatalog',
        name: 'Numuv plans',
        url: `${SITE}/pricing`,
        itemListElement: [
          {
            '@type': 'Offer',
            name: 'Starter',
            price: '0',
            priceCurrency: 'INR',
            description: 'Free forever. Up to 50 candidate profiles, 20 AI interview minutes with Jay, basic reporting, email support. No card required.',
            url: `${SITE}/pricing#starter`,
          },
          {
            '@type': 'Offer',
            name: 'Growth',
            priceCurrency: 'INR',
            description: 'Pay per AI action using Mu Points credits. Unlimited candidate profiles, pay-per-use Jay interviews, vector matching and hot tags, priority support, team hierarchy and permissions.',
            url: `${SITE}/pricing#growth`,
            priceSpecification: {
              '@type': 'PriceSpecification',
              priceCurrency: 'INR',
              description: 'Usage-based: Mu Points are topped up in INR and spent per AI action.',
            },
          },
          {
            '@type': 'Offer',
            name: 'Enterprise',
            priceCurrency: 'INR',
            description: 'Custom pricing. Everything in Growth plus SSO and audit logs, custom SLAs, a dedicated success manager, API access and data residency.',
            url: `${SITE}/contact-us`,
          },
        ],
      },
      faqJsonLd(FAQ),
    ],
  });
  return (
    <MarketingPage
      faq={FAQ}
      seoTitle="Pricing — pay only for the AI actions you use"
      seoDescription="Numuv pricing built on Mu Points. Start free, pay per AI action (matches, screenings, interviews). Enterprise plans available for staffing groups and hiring teams."
      seoPath="/pricing"
      seoKeywords="Numuv pricing, AI recruitment pricing, Mu Points, hiring software cost, pay per use recruitment"
      jsonLd={jsonLd}
      eyebrow="Pricing"
      title="Fair, flexible pricing that scales with you"
      subtitle="No seat traps, no annual commitments. Buy Mu Points once, then spend them on the AI actions that actually move your hiring forward."
    >
      <div className="grid md:grid-cols-3 gap-5 md:gap-6 mt-4">
        {TIERS.map((t) => (
          <ContentCard key={t.name} id={t.id} className={`relative ${t.highlighted ? '!p-8 ring-2 ring-[#6D5EF7]/40' : ''}`}>
            {t.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full aurora-btn px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white">Most popular</span>
            )}
            <h3 className="dis text-[20px] md:text-[22px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{t.name}</h3>
            <p className="mt-1.5 t3 text-[13.5px] leading-[20px]">{t.desc}</p>
            <p className="dis mt-5 text-[32px] md:text-[38px] font-bold tracking-[-0.02em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>{t.price}</p>
            <ul className="mt-5 space-y-2.5 text-[13.5px] md:text-[14px] t2">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-[#12B6A6] shrink-0" />{f}</li>
              ))}
            </ul>
            <Link to={t.name === 'Enterprise' ? '/contact-us' : '/login'} className={`mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-[14px] font-semibold ${t.highlighted ? 'aurora-btn text-white' : 'card thm t1'}`} data-testid={`tier-cta-${t.name.toLowerCase()}`}>
              {t.cta}
            </Link>
          </ContentCard>
        ))}
      </div>

      <ContentCard className="mt-10" id="mu-points">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">
          <Coins className="w-4 h-4" /> How Mu Points work
        </div>
        <p className="mt-3 t2 text-[14.5px] leading-[24px] max-w-[820px]">
          Mu Points are Numuv&apos;s usage credits. Every AI action — matching a candidate, generating screening questions,
          running a Jay interview, exporting a report — deducts a small, predictable amount. You always see the cost before
          you commit, and unused points never expire.
        </p>
      </ContentCard>

      <div className="mt-8 text-center t3 text-[13px] flex items-center justify-center gap-2">
        <Sparkles className="w-4 h-4 text-[#6D5EF7]" /> Questions? Email <a className="underline underline-offset-4" href="mailto:hello@numuv.app">hello@numuv.app</a>
      </div>
    </MarketingPage>
  );
}
